import clsx, { ClassValue } from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Pressable,
  StyleProp,
  Text,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native";
import { twMerge } from "tailwind-merge";

export type IconProps = Readonly<{
  size?: number;
  color?: string;
  className?: string;
}>;

type ButtonVariant = "app" | "nav" | "pill";

type ScButtonProps = Readonly<{
  text: string;
  textClassName?: string;

  onPress?: () => void;

  icon?: React.ReactElement<IconProps> | null;
  iconPosition?: string;

  variant?: ButtonVariant;
  active?: boolean;

  /** Trailing slot for the `nav` variant — e.g. an unread-count badge. */
  badge?: React.ReactNode;

  backgroundColor?: string;
  textColor?: string;
  mode?: "contained" | "outlined" | "text";
  disabled?: boolean;
  loading?: boolean;

  style?: StyleProp<ViewStyle>;
  className?: string;
  disabledClassName?: string;
}>;

const ScButton = ({
  text,
  onPress,
  icon,
  iconPosition = "left",
  variant = "app",
  active = false,
  badge,
  mode = "contained",
  disabled = false,
  loading = false,
  style,
  className = "",
  textClassName = "",
}: ScButtonProps) => {
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;
  const [isHovered, setIsHovered] = useState(false);

  const translatedText = text ? t(text) : "";
  const cx = (...args: ClassValue[]) => twMerge(clsx(...args));

  if (variant === "nav") {
    const coloredIcon =
      icon && React.isValidElement(icon)
        ? React.cloneElement(icon, {
            className: cx(
              "shrink-0",
              active ? "text-primary" : "text-ash-2",
              icon.props.className,
            ),
          })
        : null;

    return (
      <Pressable
        onPress={onPress}
        className={`${active ? "bg-primary-1" : "bg-transparent"} flex-row px-3 justify-center items-center lg:justify-start flex-1 py-2 gap-3 rounded-[16px] w-full`}
      >
        <View>{coloredIcon}</View>

        <Text
          className={`font-medium hidden lg:block ${active ? "text-primary" : "text-slate"}`}
        >
          {translatedText}
        </Text>

        {/* Pushed to the trailing edge so badges line up across nav rows
            rather than sitting at each label's ragged right. */}
        {badge ? <View className="ml-auto">{badge}</View> : null}
      </Pressable>
    );
  }

  // App button variant
  const isOutlined = mode === "outlined";
  // Hover/press feedback only on a button that can actually be pressed: the
  // `hover:opacity-*` variants outrank `opacity-50`, so a disabled button
  // used to snap back to full strength whenever it was hovered.
  const isInteractive = !disabled && !loading;

  const combinedClassName = cx(
    "rounded-2xl flex-row items-center justify-center gap-2 px-4 py-2.5 md:py-3 bg-primary",
    isInteractive && "active:opacity-80 hover:opacity-90",
    iconPosition === "right" ? "flex-row-reverse" : "flex-row",
    isOutlined && "bg-transparent border border-primary",
    isOutlined && isInteractive && "hover:bg-primary-2 hover:opacity-100",
    disabled && "opacity-50",
    className,
  );
  const resolvedIcon =
    icon && React.isValidElement(icon) && !loading
      ? React.cloneElement(icon, {
          size: icon.props.size ?? 20,
          className: cx(
            "shrink-0",
            disabled
              ? "text-ash-2"
              : isOutlined
                ? "text-primary"
                : "text-white",
            iconPosition === "right" ? "ml-2" : "mr-2",
            icon.props.className,
          ),
        })
      : null;

  if (variant === "pill") {
    const showHover = isHovered && !disabled;
    const coloredIcon =
      icon && React.isValidElement(icon)
        ? React.cloneElement(icon, {
            color: showHover ? "white" : "primary",
            size: icon.props.size ?? 14,
          })
        : null;
    return (
      <Pressable
        onPress={onPress}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        disabled={disabled}
        className={`flex-row items-center justify-center gap-2 px-3 py-1.5 rounded-xl border border-ash-3 ${
          disabled ? "" : "active:opacity-80"
        } ${showHover ? "bg-primary" : "bg-primary/10"} ${
          disabled ? "opacity-50" : ""
        } ${className}`}
      >
        {coloredIcon && iconPosition !== "right" && coloredIcon}
        <Text
          className={`text-xs font-semibold ${showHover ? "text-white" : "text-primary"}`}
        >
          {translatedText}
        </Text>
        {coloredIcon && iconPosition === "right" && coloredIcon}
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      className={combinedClassName}
      style={[{ borderRadius: isMobile ? 12 : 16 }, style]}
    >
      {loading ? (
        <ActivityIndicator
          className={isOutlined ? "text-primary" : "text-white"}
        />
      ) : (
        <>
          {resolvedIcon}
          <Text
            className={cx(
              "font-medium",
              isOutlined ? "text-primary" : "text-white",
              textClassName,
            )}
          >
            {translatedText}
          </Text>
        </>
      )}
    </Pressable>
  );
};

export default ScButton;
